import { ChakraProvider } from "@chakra-ui/react";
import { Fonts, theme } from "@airhelp/react";
import { storyblokInit } from "@storyblok/react";
import { LocalizationContextProvider } from "contexts/LocalizationContext";
import { SettingsContextProvider } from "contexts/SettingsContext";
import useRollbar from "hooks/useRollbar";
import useUserTrackingCookie from "hooks/useUserTrackingCookie";
import CookiesNoticeAdapter from "modules/common/components/CookiesNoticeAdapter";
import type { AppProps } from "next/app";
import Error from "next/error";
import Head from "next/head";
import { useEffect } from "react";
import { CookiesProvider } from "react-cookie";
import type { PageProps } from "types/global";
import { TrackingScripts } from "components/TrackingScripts"; // imported directly to not load all the components into one chunk

const App = ({ Component, pageProps }: AppProps<PageProps & { errorCode: number | null; pageReferrer: string }>) => {
  const {
    translations,
    language = "en",
    errorCode = null,
    isTrackingless = false,
    settings = {},
    pageReferrer,
  } = pageProps;

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useUserTrackingCookie();
  useRollbar();
  storyblokInit();

  if (errorCode) return <Error statusCode={errorCode} />;

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </Head>
      <TrackingScripts {...{ language, isTrackingless, pageReferrer }} />
      <CookiesProvider>
        <ChakraProvider theme={theme}>
          <Fonts />
          <SettingsContextProvider settings={settings}>
            <LocalizationContextProvider language={language} translations={translations}>
              <Component {...pageProps} />
              <CookiesNoticeAdapter />
            </LocalizationContextProvider>
          </SettingsContextProvider>
        </ChakraProvider>
      </CookiesProvider>
    </>
  );
};

export default App;
